import { captureException } from '@sentry/react';
import { useState, useCallback, useEffect, useRef } from 'react';

interface UseSpeechRecognitionProps {
  onSendMessage?: (message: string) => void;
  isFromChatInput?: boolean;
  isFromPractice?: boolean;
}

export function useSpeechRecognition({
  onSendMessage,
  isFromChatInput,
  isFromPractice,
}: UseSpeechRecognitionProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [speechError, setSpeechError] = useState<string | null>(null);
  const [isSupported, setIsSupported] = useState<boolean>(true);
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState<SpeechRecognition | null>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSpeechTimeRef = useRef<number | null>(null);
  const isRecordingRef = useRef<boolean>(false);
  const SILENCE_TIMEOUT_MS = isFromChatInput ? 2500 : 3500;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        setIsSupported(true);
        const recognition = new SpeechRecognition();
        // Use continuous mode so multi-word sentences are not prematurely aborted
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';
        recognition.maxAlternatives = 1; // add maxAlternatives for more accurate results
        
        recognition.onstart = () => {
          setIsStarting(false);
          setIsRecording(true);
          setSpeechError(null);
          isRecordingRef.current = true;
        };

        recognition.onresult = (event) => {
          const transcript = Array.from(event.results)
            .map((result) => result[0].transcript)
            .join('');
          setTranscript(transcript);
          
          lastSpeechTimeRef.current = Date.now();
          
          // Clear any existing timeout
          if (silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
            silenceTimeoutRef.current = null;
          }
          
          // Set a new timeout to stop after silence period
          silenceTimeoutRef.current = setTimeout(() => {
            if (recognition && isRecordingRef.current) {
              recognition.stop();
            }
          }, SILENCE_TIMEOUT_MS);
        };

        recognition.onerror = (event) => {
          console.error('SpeechRecognition error:', event.error);
          setSpeechError(event.error);
          setIsStarting(false);
          setIsRecording(false);
          isRecordingRef.current = false;
          if (silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
            silenceTimeoutRef.current = null;
          }
        };

        recognition.onend = () => {
          setIsStarting(false);
          setIsRecording(false);
          isRecordingRef.current = false;
          if (silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
            silenceTimeoutRef.current = null;
          }
          lastSpeechTimeRef.current = null;
        };

        // Do not abort recognition abruptly on onspeechend in mobile
        recognition.onspeechend = () => {
          // Let continuous recognition deliver full transcript or stop via silence timeout/button
        };

        recognition.onspeechstart = () => {
          if (silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
            silenceTimeoutRef.current = null;
          }
        };

        setRecognition(recognition);
        recognitionRef.current = recognition; // Set the ref as well
      } else {
        setIsSupported(false);
        console.error('SpeechRecognition is not available in this environment.');
      }
    } else {
      setIsSupported(false);
      console.error('SpeechRecognition is not available in this environment.');
    }
    
    // Cleanup timeout on unmount
    return () => {
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }
    };
  }, [isFromChatInput, isFromPractice]);

  const startRecording = useCallback(() => {
    if (recognition) {
      // Prevent starting if already recording
      if (isRecordingRef.current || isRecording) {
        console.warn('SpeechRecognition is already recording. Ignoring start request.');
        return;
      }
      
      // Clear any existing timeout
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = null;
      }
      lastSpeechTimeRef.current = null;
      isRecordingRef.current = true;
      setIsStarting(true);
      
      try {
        recognition.start();
        setTranscript('');
      } catch (error: any) {
        // Handle the case where recognition is already started
        if (error.name === 'InvalidStateError' || error.message?.includes('already started')) {
          console.warn('SpeechRecognition was already started. Resetting state.');
          isRecordingRef.current = false;
          setIsRecording(false);
          setIsStarting(false);
          // Try to stop and restart after a brief delay
          try {
            recognition.stop();
            setTimeout(() => {
              if (!isRecordingRef.current) {
                setIsStarting(true);
                recognition.start();
                setTranscript('');
              }
            }, 100);
          } catch (stopError) {
            console.error('Error stopping recognition:', stopError);
            setIsStarting(false);
          }
        } else {
          console.error('Error starting recognition:', error);
          isRecordingRef.current = false;
          setIsRecording(false);
          setIsStarting(false);
        }
      }
    } else {
      console.error('SpeechRecognition is not initialized.');
    }
  }, [recognition, isRecording]);

  const stopRecording = useCallback(() => {
    if (recognition) {
      // Clear timeout when manually stopping
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = null;
      }
      lastSpeechTimeRef.current = null;
      isRecordingRef.current = false;
      setIsStarting(false);
      
      recognition.stop();
      setIsRecording(false);
    } else {
      console.error('SpeechRecognition is not initialized.');
    }
  }, [recognition]);

  const startListening = useCallback(() => {
    if (!recognitionRef.current) {
      console.error('SpeechRecognition is not initialized.');
      return;
    } else {
      console.log('SpeechRecognition is initialized.');
    }
  
    recognitionRef.current.start();
    setIsListening(true);
  }, []);

  // useEffect(() => {
  //   if (typeof window !== 'undefined') {
  //     const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  //     if (SpeechRecognition) {
  //       const recognition = new SpeechRecognition();
  //       recognition.continuous = false; // Test with true if needed
  //       recognition.interimResults = true;
  //       recognition.lang = 'en-US';
  //       recognition.maxAlternatives = 1;
  
  //       recognition.onstart = () => {
  //         console.log('Speech recognition started.');
  //         setIsListening(true);
  //       };
  
  //       recognition.onresult = (event) => {
  //         console.log('Speech recognition result:', event);
  //         const lastResult = event.results[event.results.length - 1];
  //         const transcript = lastResult[0].transcript;
  
  //         if (lastResult.isFinal) {
  //           console.log('Final transcript:', transcript);
  //           onResult(transcript);
  //         } else {
  //           console.log('Interim transcript:', transcript);
  //           onInterimResult?.(transcript);
  //         }
  //       };
  
  //       recognition.onerror = (event) => {
  //         console.error('SpeechRecognition error:', event.error);
  //         setIsListening(false);
  //         onEnd?.();
  //       };
  
  //       recognition.onend = () => {
  //         console.log('Speech recognition ended.');
  //         setIsListening(false);
  //         onEnd?.();
  //       };
  
  //       recognitionRef.current = recognition;
  //     } else {
  //       console.error('SpeechRecognition is not supported in this environment.');
  //     }
  //   }
  // }, [lang, onResult, onInterimResult, onEnd]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      console.log('Stopping speech recognition.');
      recognitionRef.current.stop();
    } else {
      console.error('recognitionRef.current has no value');
    }
  }, []);


  return {
    isRecording,
    isStarting,
    transcript,
    speechError,
    isSupported,
    startRecording,
    stopRecording,
    isListening,
    setIsListening,
    startListening,
    stopListening
  };
}
